import React from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ReactComponent as Dropdown } from "../../../../../assets/images/dropdown-arrow.svg";
import { ReactComponent as Cancel } from "../../../../../assets/images/TicketImages/cancel.svg";
import {
  fieldLabelSx,
  inputSx,
  selectSx,
  textareaSx,
  TEAM_TEXTAREA_MAX_ROWS,
  TEAM_TEXTAREA_MIN_ROWS,
  peopleSelectMenuProps,
  peopleSelectSx,
  teamGroupSelectMenuProps,
} from "../teamFormStyles";
import { getChannelSelectOptions } from "../../../../../utils/teamMappers";
import { TeamFormFieldSkeleton } from "../TeamLoadingSkeletons";

const CHANNEL_OPTIONS = getChannelSelectOptions();

function GroupChip({ label, onRemove }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        borderRadius: "20px",
        border: "1px solid #C5CED6",
        background: "#F9FAFA",
        boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
      }}
    >
      <Typography sx={{ fontSize: 14, color: "#405261", fontFamily: "Inter" }}>
        {label}
      </Typography>
      <Box
        component="button"
        type="button"
        onClick={onRemove}
        sx={{
          border: "none",
          background: "transparent",
          p: 0,
          display: "flex",
          cursor: "pointer",
          lineHeight: 0,
        }}
      >
        <Cancel style={{ width: 14, height: 14 }} />
      </Box>
    </Box>
  );
}

export default function TeamEditForm({ form, onChange, groupOptions = [], groupsLoading = false }) {
  const set = (key) => (e) => onChange({ ...form, [key]: e.target.value });

  const handleChannelsChange = (e) => {
    onChange({ ...form, channels: e.target.value });
  };

  const handleGroupSelect = (e) => {
    const value = e.target.value;
    if (!value || (form.groupIds || []).includes(value)) return;
    onChange({
      ...form,
      groupIds: [...(form.groupIds || []), value],
      groupSelect: "",
    });
  };

  const removeGroupId = (id) => {
    onChange({
      ...form,
      groupIds: (form.groupIds || []).filter((gid) => gid !== id),
    });
  };

  const groupLabel = (id) => groupOptions.find((g) => g.id === id)?.label || id;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography sx={fieldLabelSx}>Team Name</Typography>
        <TextField
          fullWidth
          placeholder="Enter team name"
          value={form.name}
          onChange={set("name")}
          sx={inputSx}
        />
      </Box>

      <Box>
        <Typography sx={fieldLabelSx}>Description</Typography>
        <TextField
          fullWidth
          multiline
          minRows={TEAM_TEXTAREA_MIN_ROWS}
          maxRows={TEAM_TEXTAREA_MAX_ROWS}
          placeholder="Team description and responsibilities"
          value={form.description}
          onChange={set("description")}
          sx={textareaSx}
        />
      </Box>

      <Box>
        <Typography sx={fieldLabelSx}>Assign channels</Typography>
        <FormControl size="small" sx={selectSx}>
          <Select
            multiple
            displayEmpty
            value={form.channels}
            onChange={handleChannelsChange}
            IconComponent={Dropdown}
            MenuProps={peopleSelectMenuProps}
            sx={peopleSelectSx}
            renderValue={(selected) => {
              if (!selected.length) {
                return (
                  <Typography component="span" sx={{ color: "#C5CED6", fontSize: 14 }}>
                    Select channels
                  </Typography>
                );
              }
              const labels = selected
                .map((v) => CHANNEL_OPTIONS.find((o) => o.value === v)?.label || v)
                .join(", ");
              return (
                <Typography component="span" noWrap sx={{ color: "#061522", fontSize: 14 }}>
                  {labels}
                </Typography>
              );
            }}
          >
            {CHANNEL_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily: "Inter", fontSize: 14 }}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <Typography sx={fieldLabelSx}>Groups</Typography>
        {groupsLoading ? (
          <TeamFormFieldSkeleton />
        ) : (
          <>
            <FormControl size="small" sx={selectSx}>
              <Select
                displayEmpty
                value={form.groupSelect}
                onChange={handleGroupSelect}
                IconComponent={Dropdown}
                MenuProps={teamGroupSelectMenuProps()}
                sx={peopleSelectSx}
                renderValue={(v) => (
                  <Typography component="span" sx={{ color: v ? "#061522" : "#C5CED6", fontSize: 14 }}>
                    {v ? groupLabel(v) : "Select Group"}
                  </Typography>
                )}
              >
                {groupOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id} sx={{ fontFamily: "Inter", fontSize: 14 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(form.groupIds || []).length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {(form.groupIds || []).map((id) => (
                  <GroupChip key={id} label={groupLabel(id)} onRemove={() => removeGroupId(id)} />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
